/**
 * @fileoverview Google Ads integration step for onboarding.
 * @module features/onboarding/components/steps/GoogleAdsStep
 */

import { ReactElement } from 'react'
import { useTranslation } from 'react-i18next'
import { ArrowRight } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { ScrollReveal } from '@/shared/components/ui'
import { GoogleAdsOAuthConnect } from '@/features/settings/components/GoogleAdsOAuthConnect'

interface GoogleAdsStepProps {
  /** Callback when Google Ads is connected successfully */
  onComplete: () => void
  /** Callback to skip this step */
  onSkip: () => void
}

/**
 * Fifth step of onboarding - Google Ads connection (optional).
 *
 * @component
 * @example
 * ```tsx
 * <GoogleAdsStep onComplete={completeStep} onSkip={skipStep} />
 * ```
 */
export function GoogleAdsStep({ onComplete, onSkip }: GoogleAdsStepProps): ReactElement {
  const { t } = useTranslation('features-onboarding')

  return (
    <div className="flex flex-col items-center justify-center max-w-md mx-auto text-center">
      {/* Icon */}
      <ScrollReveal animateOnMount blur delay={0}>
        <div className="w-16 h-16 rounded-2xl bg-card border border-border/15 flex items-center justify-center mb-6">
          <img src="/adwords.webp" alt="Google Ads" className="w-10 h-10" />
        </div>
      </ScrollReveal>

      {/* Heading */}
      <ScrollReveal animateOnMount blur delay={0.1}>
        <h2 className="text-3xl font-bold tracking-tight text-foreground mb-4">
          {t('googleAds.title', 'Connect Google Ads')}
        </h2>
      </ScrollReveal>

      <ScrollReveal animateOnMount blur delay={0.15}>
        <p className="text-muted-foreground mb-8">
          {t('googleAds.subtitle', 'Track your ad spend automatically')}
        </p>
      </ScrollReveal>

      {/* OAuth button */}
      <ScrollReveal animateOnMount blur delay={0.2} className="w-full">
        <div className="space-y-4">
          <GoogleAdsOAuthConnect onConnected={onComplete} showSuccessToasts={false} />

          <div className="flex items-center gap-4">
            <div className="flex-1 h-px bg-border" />
            <span className="text-xs text-muted-foreground uppercase">
              {t('common.or', 'or')}
            </span>
            <div className="flex-1 h-px bg-border" />
          </div>

          <Button variant="ghost" onClick={onSkip} className="w-full opacity-50">
            {t('googleAds.skip', 'Skip for now')}
            <ArrowRight className="h-4 w-4 ml-2" />
          </Button>
        </div>
      </ScrollReveal>
    </div>
  )
}
